import type { DraftJson } from '@apps/card/routes/CardCustomizer/DraftJson.types';
import { CardEnvelopeReturnAddressLayout, CardPaperType } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import type { LayerData } from '@apps/card/routes/CardCustomizer/components/Layer.types';
import TRADITIONAL_FRONT from '@apps/card/routes/CardCustomizer/steps/Envelope/templates/traditional.front.return.json';
import { DEFAULT_ENVELOPE_THEME_ID } from '@apps/card/routes/CardCustomizer/steps/Envelope/envelopeTemplateUtils';
import type { CardDashboardStationeryTemplatesContainerQuery, CardDashboardStationeryTemplatesSummaryQuery } from './DesignsGallery.graphql.generated';
import type { StationeryDesignGalleryTemplateFragment } from '@apps/card/types/stationeryTemplate.graphql.generated';
import { StationeryTemplateCategoryEnum, ThemeVariationType } from '@graphql/generated-base';
import type { DigitalDesignTemplateCategory, StationeryTemplate, ValidDigitalDesignApiData } from '@apps/card/types/stationeryTemplate.types';
import { SAVE_THE_DATE_PHOTO_THEMES } from '@apps/admin/saveTheDate/constants';
import { PLACEHOLDER_TEMPLATE } from '@apps/card/routes/CardCustomizer/components/CardBackChoiceGroup';
import { isEmpty } from 'lodash-es';
import { getOrientation } from '@apps/card/utils';
import { prepareFacetOptions, mapToFacetOption } from './components/Filters/facetOptions';
import type { FacetOptionLocal } from './components/Filters/Filter.types';
import type { StationeryProduct, StationeryProductAttribute } from '@shared/utils/print-price/types';
import { isPostcard, isPostcardCardSize, isPostcardCategory } from '@apps/card/utils/cardSizeName';

const CATEGORIES_WITHOUT_BACK = [StationeryTemplateCategoryEnum.menu, StationeryTemplateCategoryEnum.napkin, StationeryTemplateCategoryEnum.sign];
export const CATEGORIES_WITHOUT_ENVELOPE = [...CATEGORIES_WITHOUT_BACK, StationeryTemplateCategoryEnum.enclosure];

// Called by DigitalCardCustomizer and usePremiumDigitalUpsell
export function applyDefaultBackTemplate(themeJSON: DraftJson, category: StationeryTemplateCategoryEnum) {
  const shouldHaveBack = !CATEGORIES_WITHOUT_BACK.includes(category);
  if (shouldHaveBack && isEmpty(themeJSON.card.back)) {
    themeJSON.card.back = PLACEHOLDER_TEMPLATE;
  }
}

export function isPhotoTemplateThemeJSON(themeJson?: DraftJson) {
  if (!themeJson) return false;

  return themeJson.card?.front?.layers?.some(layer => layer.type === 'image' && layer.editable) ?? false;
}

const getOptionIdsForAttribute = (attributes: ReadonlyArray<StationeryProductAttribute>, attributeId: string): string[] => {
  const attribute = attributes.find(attr => attr.attributeId === attributeId);
  return attribute?.attributeOptions.map(opt => opt.attributeOption.id) ?? [];
};

type ResetCardTemplateDefaultsTemplate = Readonly<{
  themeJSON: DraftJson;
  products?: ReadonlyArray<StationeryProduct>;
}>;

type ResetCardTemplateDefaultsOptions = Readonly<{
  resetPaperType?: boolean;
}>;

// For MVP, we baked the default card back and envelope into every template.
// Moving forward, new templates will not contain a default card back and envelope.
// This fills/overwrites in the default data, so it doesn't matter what was baked into the template.
// This also sets the default paper type for photo templates to semi-gloss.
export function resetCardTemplateDefaults(template: ResetCardTemplateDefaultsTemplate, category?: StationeryTemplateCategoryEnum, options?: ResetCardTemplateDefaultsOptions) {
  const { themeJSON, products } = template;
  const { resetPaperType = true } = options ?? {};
  const shouldHaveBack = !CATEGORIES_WITHOUT_BACK.includes(category as StationeryTemplateCategoryEnum);
  themeJSON.card.back = shouldHaveBack ? PLACEHOLDER_TEMPLATE : undefined;
  themeJSON.card.envelope.layout = CardEnvelopeReturnAddressLayout.front;
  themeJSON.card.envelope.design = DEFAULT_ENVELOPE_THEME_ID;
  themeJSON.card.envelope.layers = TRADITIONAL_FRONT as LayerData[];

  if (resetPaperType && isPhotoTemplateThemeJSON(themeJSON)) {
    const productAttributes = products?.flatMap(p => p.attributes) ?? [];
    const paperTypeId = 'paperType';
    const availablePaperTypes = getOptionIdsForAttribute(productAttributes, paperTypeId);
    if (availablePaperTypes.includes(CardPaperType.semiGloss)) {
      themeJSON.customizations.paperType = CardPaperType.semiGloss;
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isValidDigitalDesign = (digitalDesign: any): digitalDesign is ValidDigitalDesignApiData => {
  return !!digitalDesign && !!digitalDesign.themeId && !!digitalDesign.thumbnailUrl && !!digitalDesign.theme?.name && !!digitalDesign.theme?.id;
};

const STATIONERY_TEMPLATE_CATEGORY_TO_THEME_VARIATION_TYPE: Partial<Record<StationeryTemplateCategoryEnum, ThemeVariationType | null>> = {
  saveTheDate: ThemeVariationType.savethedate,
  invitation: ThemeVariationType.invite,
  thankYou: null,
  holiday: null,
  enclosure: null,
};

const JOY_MARKETING_VENDOR = 'Joy';

type DesignServiceThemeData = {
  id: string;
  themeId: string;
  name: string;
  marketingData: CardDashboardStationeryTemplatesContainerQuery['ecardDesigns'][number]['marketingData'];
};

export const findMatchingMarketingData = (theme: DesignServiceThemeData, category: StationeryTemplateCategoryEnum) => {
  return theme.marketingData?.find(
    data =>
      // Ignoring partner designs for now
      data.vendor === JOY_MARKETING_VENDOR &&
      // `marketingData` is an array of related designs
      // eg. if `themeId` === 'arch_ii', marketingData will include 'arch_i', 'arch_ii', 'arch_iii'
      data.variantThemeId === theme.themeId &&
      // In modern ecard customizer, we're currently requesting all themes regardless of variation type --
      // which means that both save the date and invitations show the same set of themes.
      //
      // However, the theme tiles in the ecard customizer show a generic thumbnail (no text/content)
      // and this won't work in the card gallery because we need thumbnails with content preview.
      data.variationType === STATIONERY_TEMPLATE_CATEGORY_TO_THEME_VARIATION_TYPE[category],
  );
};

export const isFoilTemplate = (template: { themeId: string }) => {
  return template.themeId.endsWith('_foil');
};

/**
 * Orientation-prefixed key used by the `printGalleryThemeDisplayOrder` flag so a
 * theme's portrait and landscape variants can be ordered independently
 * (e.g. `wide mirrorball`, `tall upload_your_design`). Square templates key on
 * the bare themeId.
 */
export const themeIdWithOrientation = <T extends { themeId: string; widthInches: number; heightInches: number }>(template: T): string => {
  if (template.widthInches > template.heightInches) return `wide ${template.themeId}`;
  if (template.heightInches > template.widthInches) return `tall ${template.themeId}`;
  return template.themeId;
};

/**
 * Parses a `printGalleryThemeDisplayOrder` entry into its parts. Entries may
 * carry an optional `tall `/`wide ` orientation prefix and an optional
 * `+colorwayId` suffix, e.g. `wide mirrorball+lilac`, `boldly+black`, `mirrorball`.
 *
 * - `orientationKey`: the theme-with-orientation string, matched against
 *   `themeIdWithOrientation(template)`.
 * - `themeId`: the bare themeId (orientation prefix stripped), used as fallback.
 * - `colorwayId`: the colorway to pre-select, if the `+` form was used.
 *
 * The `+` delimiter is explicit on purpose: themeIds themselves contain `_`
 * (`modern_lines`, `upload_your_design`) and some color-suffixed strings are real
 * legacy themeIds (`emilio_sage`), so `_` cannot be parsed unambiguously.
 */
export const parseDisplayOrderEntry = (entry: string): { orientationKey: string; themeId: string; colorwayId?: string } => {
  const plusIndex = entry.indexOf('+');
  const beforeColor = (plusIndex === -1 ? entry : entry.slice(0, plusIndex)).trim();
  const colorwayId = plusIndex === -1 ? undefined : entry.slice(plusIndex + 1).trim() || undefined;
  const themeId = beforeColor.replace(/^(tall|wide)\s+/, '');
  return { orientationKey: beforeColor, themeId, colorwayId };
};

type ExpandableTemplate = Readonly<{
  id: string;
  themeId: string;
  widthInches: number;
  heightInches: number;
  themeJSON?: DraftJson;
}>;

/**
 * Expands a template list into the gallery-order feature flag's sequence,
 * honoring repeats and per-instance colorways (used only when search is inactive).
 *
 * For each flag entry: resolve the template (by orientation key, then bare
 * themeId), pre-select the `+colorway` if valid, and emit an instance — so a
 * theme listed N times yields N tiles. Templates not named by the flag are
 * appended once each, in their original order. An optional `priorityThemeId` is
 * promoted to the front (mirrors the non-flag catalog behavior).
 */
export const expandTemplatesByDisplayOrder = <T extends ExpandableTemplate>(
  items: ReadonlyArray<T>,
  themeOrder: ReadonlyArray<string>,
  priorityThemeId?: string,
): Array<T & { colorwayOverride?: string; instanceKey: string }> => {
  const byOrientationKey = new Map<string, T>();
  const byThemeId = new Map<string, T>();
  for (const item of items) {
    const orientationKey = themeIdWithOrientation(item);
    if (!byOrientationKey.has(orientationKey)) byOrientationKey.set(orientationKey, item);
    if (!byThemeId.has(item.themeId)) byThemeId.set(item.themeId, item);
  }

  const usedIds = new Set<string>();
  const result: Array<T & { colorwayOverride?: string; instanceKey: string }> = [];

  themeOrder.forEach((entry, flagIndex) => {
    const { orientationKey, themeId, colorwayId } = parseDisplayOrderEntry(entry);
    // Orientation-prefixed entries (`wide X`/`tall X`) must match only that
    // orientation — never fall back to a differently-oriented variant. Bare
    // entries (`orientationKey === themeId`) may match any orientation.
    const item = byOrientationKey.get(orientationKey) ?? (orientationKey === themeId ? byThemeId.get(themeId) : undefined);
    if (!item) return;

    let colorwayOverride: string | undefined;
    if (colorwayId) {
      const options = item.themeJSON?.card?.front?.colorways?.options;
      if (options?.some(option => option.id === colorwayId)) {
        colorwayOverride = colorwayId;
      } else if (process.env.NODE_ENV !== 'production') {
        console.warn(`printGalleryThemeDisplayOrder: colorway "${colorwayId}" not found for theme "${item.themeId}" (entry "${entry}")`);
      }
    }

    usedIds.add(item.id);
    result.push({ ...item, colorwayOverride, instanceKey: `${item.id}#${colorwayId ?? 'default'}#${flagIndex}` });
  });

  // Templates not referenced by the flag follow after, once each, in original order.
  for (const item of items) {
    if (!usedIds.has(item.id)) result.push({ ...item, instanceKey: item.id });
  }

  if (priorityThemeId) {
    const priorityIndex = result.findIndex(instance => instance.themeId === priorityThemeId);
    if (priorityIndex > 0) {
      const [promoted] = result.splice(priorityIndex, 1);
      result.unshift(promoted);
    }
  }

  return result;
};

type TileGroupTemplate = Pick<StationeryDesignGalleryTemplateFragment, 'themeId' | 'widthInches' | 'heightInches' | 'category'>;

/**
 * Key for the theme+orientation+category group whose templates compete for a single
 * gallery tile. Shared by the tile dedupe (`filterTemplatesByOrientation`) and the
 * size-facet tagging (`collectSizeFacetTags`) so the surviving tile can carry the
 * size tags of its entire group.
 */
export const templateTileGroupKey = (t: TileGroupTemplate): string => `${t.themeId}@${getOrientation(t.widthInches, t.heightInches)}@${t.category}`;

export const filterTemplatesByOrientation = <T extends TileGroupTemplate>(templates: T[]) => {
  // We want the lowest price to "win". Sort those last. When inserted into the map last one wins.
  // Exception: postcards (4x6) defer the tile to their classic 5x7 sibling — the
  // postcard stays reachable through the PDP size picker. Applies to every
  // postcard category (thank-you and save-the-date).
  const isPostcardTile = (t: T) => isPostcard(t.category, t.widthInches, t.heightInches);
  const sortedTemplates = templates.slice();
  sortedTemplates.sort((a, b) => {
    const postcardRank = (isPostcardTile(a) ? 0 : 1) - (isPostcardTile(b) ? 0 : 1);
    if (postcardRank !== 0) return postcardRank;
    return b.widthInches - a.widthInches;
  });
  const templatesByOrientation = new Map(sortedTemplates.map(t => [templateTileGroupKey(t), t]));

  return Array.from(templatesByOrientation.values());
};

// Size facet is scoped to postcard categories (thank-you, save-the-date) — the
// ones with named sibling sizes (classic 5x7 vs Postcard 4x6). Because facets are
// derived from the tags present on templates (`useFilterFacets`), leaving other
// categories untagged keeps the Size filter from appearing there at all.
const sizeFacetTag = (t: TileGroupTemplate): FacetOptionLocal | undefined => {
  if (!isPostcardCategory(t.category)) return undefined;
  return isPostcardCardSize(t.widthInches, t.heightInches) ? 'size:postcard' : 'size:classic';
};

/**
 * Size facet tags per tile group, computed over the FULL pre-dedupe template list.
 * The tile dedupe (`filterTemplatesByOrientation`) runs before filtering, so the
 * surviving tile (the classic 5x7) must carry a tag for every size available in its
 * theme+orientation+category group — filtering by "Postcard 4x6" then surfaces
 * themes whose postcard is reachable via the PDP size picker even though the tile
 * faces the 5x7.
 */
export const collectSizeFacetTags = (templates: ReadonlyArray<TileGroupTemplate>): Map<string, FacetOptionLocal[]> => {
  const tagsByGroup = new Map<string, Set<FacetOptionLocal>>();
  for (const template of templates) {
    const tag = sizeFacetTag(template);
    if (!tag) continue;
    const key = templateTileGroupKey(template);
    const tags = tagsByGroup.get(key) ?? new Set<FacetOptionLocal>();
    tags.add(tag);
    tagsByGroup.set(key, tags);
  }
  return new Map(Array.from(tagsByGroup.entries(), ([key, tags]) => [key, Array.from(tags).sort()]));
};

/**
 * `product` facet tags, derived from the products linked to a template (available only
 * after `injectProducts`). Today the only option is `product:magnet`, which lets the
 * gallery nav deep-link to magnet-bearing themes via `?pf=product%3Dmagnet`. With a
 * single option the facet never renders a pill (`useResponsiveFilterOverflow`).
 */
export const productFacetTags = (products: ReadonlyArray<{ productType: string }>): FacetOptionLocal[] => {
  return products.some(product => product.productType === 'magnet') ? ['product:magnet'] : [];
};

/**
 * Product ids per tile group, computed over the FULL pre-dedupe template list — the
 * product-facet counterpart of `collectSizeFacetTags`. The magnet product links to the
 * 4x6/6x4 postcard templates, never to the classic 5x7 that wins the tile, so a tile
 * tagged from its own products alone would hide every magnet-bearing theme except the
 * postcard-only ones (prod, 2026-10-07: 2 of 41 save-the-date themes, 0 of 35 thank-you).
 */
export const collectTileGroupProductIds = (templates: ReadonlyArray<TileGroupTemplate & { productIds: ReadonlyArray<string> }>): Map<string, string[]> => {
  const idsByGroup = new Map<string, Set<string>>();
  for (const template of templates) {
    const key = templateTileGroupKey(template);
    const ids = idsByGroup.get(key) ?? new Set<string>();
    template.productIds.forEach(id => ids.add(id));
    idsByGroup.set(key, ids);
  }
  return new Map(Array.from(idsByGroup.entries(), ([key, ids]) => [key, Array.from(ids).sort()]));
};

/**
 * `product` facet tags for a gallery tile: from the whole tile group's products when the
 * template carries `tileGroupProductIds`, else from its own `products` (templates built
 * outside the gallery pipeline, e.g. favorites).
 */
export const tileGroupProductFacetTags = (
  template: Pick<StationeryTemplate, 'products' | 'tileGroupProductIds'>,
  catalog: ReadonlyArray<{ id: string; productType: string }>,
): FacetOptionLocal[] => {
  const groupIds = template.tileGroupProductIds;
  if (!groupIds) return productFacetTags(template.products);
  return productFacetTags(catalog.filter(product => groupIds.includes(product.id)));
};

export type StationeryTemplateWithProductIds = Omit<StationeryTemplate, 'products'> & { productIds: string[]; tileGroupProductIds: string[] };

/** The gallery query result, or the server-rendered summary of it (same shape, nodes without `themeJSON`). */
export type GalleryTemplatesQueryData = CardDashboardStationeryTemplatesContainerQuery | CardDashboardStationeryTemplatesSummaryQuery;

type ProcessInitialQueryDataOptions = Readonly<{
  /**
   * `data` is the server-rendered summary (PRNT-5494), which carries no digital designs. Format and
   * price come from matching a template to its digital counterpart, so the summary leaves them
   * undecided (basic) rather than badge every basic design Premium until the full query answers.
   */
  isSummary?: boolean;
}>;

export const processInitialQueryData = (data: GalleryTemplatesQueryData | undefined, category: StationeryTemplateCategoryEnum, options: ProcessInitialQueryDataOptions = {}) => {
  const { isSummary = false } = options;
  const templates = (data?.stationeryTemplatesContainer?.pagedResults?.edges || []).map(t => t.node);

  const printTemplatesByTheme = new Map(templates.map(t => [t.themeId, t]));

  const templatesWithMatchingDigitalDesignMap = new Map<string, ValidDigitalDesignApiData>();
  const digitalOnlyDesigns: StationeryTemplateWithProductIds[] = [];

  if (category === StationeryTemplateCategoryEnum.saveTheDate) {
    // TODO: photo ecards should be sent from card_service
    // https://withjoy.atlassian.net/browse/PRNT-1107
    SAVE_THE_DATE_PHOTO_THEMES.forEach(theme => {
      const printableThemeConfig = printTemplatesByTheme.get(theme.themeId);
      if (printableThemeConfig) {
        templatesWithMatchingDigitalDesignMap.set(theme.themeId, {
          themeId: theme.themeId,
          thumbnailUrl: theme.thumbnailUrl,
          category,
          name: theme.name,
          isPremiumDesign: printableThemeConfig.premium,
        });
      }
    });
  }

  const ecardDesigns = data && 'ecardDesigns' in data ? data.ecardDesigns : undefined;
  if (ecardDesigns) {
    ecardDesigns.forEach(design => {
      const designThemeId = design.themeId;
      const designName = design.name;
      const digitalDesignConfig = findMatchingMarketingData(design, category);

      if (digitalDesignConfig) {
        const thumbnailUrl = digitalDesignConfig.imageUrl;
        const printableThemeConfig = printTemplatesByTheme.get(designThemeId);
        // Check if the digital design is also printable
        if (printableThemeConfig) {
          // if printable, track the digital design
          templatesWithMatchingDigitalDesignMap.set(designThemeId, {
            themeId: designThemeId,
            thumbnailUrl,
            name: designName,
            category: category as DigitalDesignTemplateCategory,
            isPremiumDesign: printableThemeConfig.premium,
          });
        } else {
          // Otherwise, store the digital design to be placed at the end of the list
          digitalOnlyDesigns.push({
            id: design.id,
            format: 'digital',
            themeId: designThemeId,
            category,
            approved: true,
            enabled: true,
            name: designName,
            thumbnailUrl: null,
            digitalDesignThumbnailUrl: thumbnailUrl,
            premium: false,
            tags: [],
            // Free ecard, always 5x7 portrait and non-foil. Include the definite
            // price/orientation/foil facets so these tiles participate in the same
            // filters as printable templates (a tile with no matching facet is excluded).
            // `format:digital` is appended post-injectProducts in `useDesignTemplates`.
            mappedTags: ['price:basic', 'orientation:portrait', 'foil:without'],
            isFavorite: false,
            isFoil: false,
            widthInches: 5, // Legacy digital cards were 5x7
            heightInches: 7,
            productIds: [],
            tileGroupProductIds: [],
          });
        }
      }
    });
  }

  prepareFacetOptions(data?.stationeryTags);

  // Computed before the tile dedupe below so the surviving tile carries the size
  // tags of every sibling in its group (see `collectSizeFacetTags`).
  const sizeTagsByTileGroup = collectSizeFacetTags(templates);
  // Same reason: the magnet product is linked to the postcard siblings, not the 5x7 tile.
  const productIdsByTileGroup = collectTileGroupProductIds(templates);

  const processedInitialPrintableTemplates: Array<StationeryTemplateWithProductIds> =
    filterTemplatesByOrientation(templates).map(template => {
      const maybeDigitalDesignConfig = templatesWithMatchingDigitalDesignMap.get(template.themeId);

      // prettier-ignore
      const mappedTagsLocal: FacetOptionLocal[] = [
        template.tags.some(tag => tag === 'photo')    ? 'photo:with'    : 'photo:without',
        template.premium || (!maybeDigitalDesignConfig && !isSummary) ? 'price:premium' : 'price:basic',
        isFoilTemplate(template)                      ? 'foil:with'     : 'foil:without',
      ];

      return {
        ...template,
        // Mapping tags to composite codes for applying filters in `useApplyFiltersToTemplates`
        // prettier-ignore
        mappedTags: [
          ...mapToFacetOption(template.tags),
          `orientation:${getOrientation(template.widthInches, template.heightInches).toLowerCase()}`,
          ...(sizeTagsByTileGroup.get(templateTileGroupKey(template)) ?? []),
          ...mappedTagsLocal
        ],
        format: maybeDigitalDesignConfig || isSummary ? 'paperAndDigital' : 'paper',
        digitalDesignThumbnailUrl: maybeDigitalDesignConfig?.thumbnailUrl,
        isFoil: isFoilTemplate(template),
        tileGroupProductIds: productIdsByTileGroup.get(templateTileGroupKey(template)) ?? template.productIds,
      };
    }) || [];

  return [...processedInitialPrintableTemplates, ...digitalOnlyDesigns];
};
